import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useUpdatePositionDetailsMutation } from '../../../../api/preHQ';
import PositionDetails from '../../positions/PositionDetails';
import { useGetApprovedPositionQuery } from '../../../../api/preHQ';
import { positionDetailsSchema, basicAttributesSchema, createCustomFieldsSchema } from './common/schema';
import { useCreatePositionWithCompaFieldsMutation } from '../../../../api/positions';
import ConfirmationModal from './components/ConfirmationModal';
import { POSITION_CREATED } from '../../../../constants';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

const Loader = () => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80vh',
            py: 3,
        }}
    >
        <CircularProgress size={60} sx={{ color: 'orange.500', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
            Loading Custom Fields...
        </Typography>
    </Box>
);

const EditAndDraft = () => {
    const [searchParams] = useSearchParams();
    const positionId = searchParams.get('positionId');
    const { data, isLoading: isCustomFieldsLoading } = useGetApprovedPositionQuery({ posId: positionId });

    // State to hold data from child components
    const [positionDetailsData, setPositionDetailsData] = useState(null);
    const [basicAttributesData, setBasicAttributesData] = useState(null);
    const [customFieldsData, setCustomFieldsData] = useState(null);

    const [validationErrors, setValidationErrors] = useState({});
    const [createPositionWithCompaFields, { isLoading: isCreateWithCompaLoading }] =
        useCreatePositionWithCompaFieldsMutation();
    const [updatePositionDetails, { isLoading: isUpdatingPositionDetails, error: updatePositionDetailsError }] =
        useUpdatePositionDetailsMutation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();

    const executeMoveToDraft = async () => {
        try {
            const payloadObject = {
                positionId: positionId,
                positionCode: positionDetailsData?.positionCode || '',
                department: basicAttributesData.department,
                designation: basicAttributesData.designation,
                location: positionDetailsData.location.id,
                startDate: '',
                budgetLowerLimit: positionDetailsData.budgetLowerLimit,
                budgetUpperLimit: positionDetailsData.budgetUpperLimit,
                budgetMidLimit: positionDetailsData.budgetMidLimit,
                'customObjectInstance.customerID': data?.extendedDef?.customerID || '',
                'customObjectInstance.baseObjectMetaClassName': data?.extendedDef?.baseObjectMetaClassName || '',
                'customObjectInstance.metaCustomObjectDefinitionUID':
                    data?.extendedDef?.metaCustomObjectDefinitionID || '',
            };

            const definitions = data?.extendedDef?.metaCustomFieldDefinitions || [];
            definitions.forEach((definition, index) => {
                const fieldLabel = definition.fieldLabel;
                const fieldValue = customFieldsData[fieldLabel] || '';
                const fieldId = definition.metaCustomFieldDefinitionID;

                payloadObject[`customObjectInstance.customFieldValues[${index}].fieldValue`] = fieldValue;
                payloadObject[`customObjectInstance.customFieldValues[${index}].metaCustomFieldDefinitionID`] = fieldId;
            });

            const payload = new FormData();
            for (const key in payloadObject) {
                payload.append(key, payloadObject[key]);
            }
            const response = await createPositionWithCompaFields(payload).unwrap();

            // onSuccess block
            if (response) {
                const result = await updatePositionDetails({
                    positionId: positionId,
                    payload: { status: POSITION_CREATED },
                }).unwrap();
                if (result) {
                    toast.success('Position Created successfully!');
                    setTimeout(() => {
                        navigate('/client/positions');
                    }, 2000);
                }
            }
        } catch (error) {
            toast.error(error.data?.message || 'Failed to move position to draft. Please try again.');
        } finally {
            setIsModalOpen(false);
        }
    };

    const handleMoveToDraft = async () => {
        // Validate Position Details
        const posDetailsResult = positionDetailsSchema.safeParse(positionDetailsData);

        // Validate Basic Attributes
        const basicAttrsResult = basicAttributesSchema.safeParse(basicAttributesData);

        // Validate Custom Fields
        const customFieldsSchema = createCustomFieldsSchema(data?.extendedDef?.metaCustomFieldDefinitions);
        const customFieldsResult = customFieldsSchema.safeParse(customFieldsData);

        const allValid = posDetailsResult.success && basicAttrsResult.success && customFieldsResult.success;
        if (allValid) {
            setValidationErrors({});
            setIsModalOpen(true);
        } else {
            toast.error('Please fill all the required fields before proceeding.');
            // Combine and set errors for display
            const errors = {
                positionDetails: !posDetailsResult.success ? posDetailsResult.error.flatten().fieldErrors : {},
                basicAttributes: !basicAttrsResult.success ? basicAttrsResult.error.flatten().fieldErrors : {},
                customFields: !customFieldsResult.success ? customFieldsResult.error.flatten().fieldErrors : {},
            };
            setValidationErrors(errors);
        }
    };

    if (isCustomFieldsLoading) {
        return <Loader />;
    }

    return (
        <div>
            <PositionDetails
                onPositionDetailsDataChange={setPositionDetailsData}
                onBasicAttributesDataChange={setBasicAttributesData}
                onCustomFieldsDataChange={setCustomFieldsData}
                handleMoveToDraft={handleMoveToDraft}
                validationErrors={validationErrors}
            />
            <ConfirmationModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={executeMoveToDraft}
                isLoading={isCreateWithCompaLoading || isUpdatingPositionDetails}
            />
        </div>
    );
};

export default EditAndDraft;
